import React from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';

export interface PillToggleItem {
  id: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
}

export interface PillToggleGroupProps {
  items: PillToggleItem[];
  value: string;
  onChange: (id: string) => void;
  ariaLabel?: string;
  /** 'sm' = padding réduit (mobile) ; 'md' = padding normal (desktop). Défaut : 'md'. */
  size?: 'sm' | 'md';
  /**
   * Si true, affiche toujours le label (même sur les items non sélectionnés).
   * Cas d'usage :
   *  - granularité texte-seul (sans icône) dans HistoryTab
   *  - mobile Positioning (icône + label visible sur tous les boutons)
   * Si false (défaut), les items non sélectionnés avec une icône l'affichent seule,
   * avec le label en Radix Tooltip.
   */
  alwaysShowLabel?: boolean;
}

/**
 * Pill toggle group unifié — style actif violet (var(--color-primary)).
 *
 * Utilisé dans :
 *  - PositioningHeader (modes chat / texte / charte)
 *  - HistoryTab (métrique + granularité temporelle)
 *
 * Convention inline styles : conforme à V4-FRONT-TAILWIND-FROZEN-CSS
 * (le CSS Tailwind du repo est figé — privilégier les styles inline pour
 * tout layout/dimension critique, avec var(--color-*) pour les couleurs).
 */
export function PillToggleGroup({
  items,
  value,
  onChange,
  ariaLabel,
  size = 'md',
  alwaysShowLabel = false,
}: PillToggleGroupProps) {
  // Padding du container selon size
  const containerPadding = size === 'sm' ? '0.125rem' : '0.25rem'; // p-0.5 / p-1

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className="flex items-center gap-1 rounded-full"
      style={{ backgroundColor: 'var(--color-surface-hover)', padding: containerPadding }}
    >
      {items.map((item) => {
        const isSelected = value === item.id;
        const showLabel = alwaysShowLabel || isSelected;
        const IconComponent = item.icon;
        const iconSizeClass = size === 'sm' ? 'w-3.5 h-3.5 shrink-0' : 'w-4 h-4 shrink-0';

        // Padding du bouton : si label visible → plus large horizontalement
        const buttonPaddingInline = showLabel
          ? (size === 'sm' ? '0.375rem 0.625rem' : '0.375rem 0.75rem')
          : (size === 'sm' ? '0.25rem 0.375rem' : '0.375rem');

        const activeStyle: React.CSSProperties = {
          backgroundColor: 'var(--color-primary)',
          color: 'var(--color-text-light)',
          boxShadow: '0 1px 3px rgba(85, 0, 255, 0.4), 0 0 0 2px rgba(85, 0, 255, 0.15)',
          padding: buttonPaddingInline,
        };
        const inactiveStyle: React.CSSProperties = {
          color: 'var(--color-text-secondary)',
          padding: buttonPaddingInline,
        };

        const button = (
          <button
            type="button"
            role="tab"
            aria-selected={isSelected}
            aria-label={item.label}
            onClick={() => onChange(item.id)}
            className="rounded-full transition-all flex items-center gap-1.5 text-xs font-medium"
            style={isSelected ? activeStyle : inactiveStyle}
          >
            {IconComponent && <IconComponent className={iconSizeClass} />}
            {showLabel && <span>{item.label}</span>}
          </button>
        );

        // Item non sélectionné avec icône et sans alwaysShowLabel → tooltip sur le label
        if (!isSelected && IconComponent && !alwaysShowLabel) {
          return (
            <Tooltip key={item.id}>
              <TooltipTrigger asChild>{button}</TooltipTrigger>
              <TooltipContent sideOffset={6}>{item.label}</TooltipContent>
            </Tooltip>
          );
        }

        return <React.Fragment key={item.id}>{button}</React.Fragment>;
      })}
    </div>
  );
}
